import { useEffect, useState } from "react";
import { Building2, Plus, Copy, Trash2, Power, Code2, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const ALL_LEAGUES = ["NHL", "NBA", "NFL", "MLB"];

function PartnerCard({ p, onToggle, onDelete }) {
  const [showSnippet, setShowSnippet] = useState(false);
  const embedUrl = `${ORIGIN}/embed?key=${p.api_key}&league=NFL`;
  const iframe = `<iframe src="${embedUrl}" width="100%" height="640" style="border:0;border-radius:12px" title="${p.brand_name} Injury Report"></iframe>`;
  const apiExample = `curl "${BACKEND}/api/partner/injuries?league=NFL" \\\n  -H "X-API-Key: ${p.api_key}"`;

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  return (
    <div data-testid={`partner-card-${p.id}`} className="bg-[#0F1420] border border-[#232D42] rounded-xl p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${p.brand_color}22` }}>
            {p.brand_logo ? <img src={p.brand_logo} alt="" className="h-6 w-6 object-contain" /> : <Building2 size={18} style={{ color: p.brand_color }} />}
          </div>
          <div className="min-w-0">
            <div className="font-heading font-semibold text-slate-100 truncate">{p.brand_name}</div>
            <div className="text-[11px] font-mono text-slate-500 truncate">{p.name} · {p.request_count || 0} calls</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${p.active ? "text-[#00E5A3] bg-[#00E5A3]/10" : "text-slate-500 bg-slate-500/10"}`}>
            {p.active ? "active" : "off"}
          </span>
          <button data-testid={`partner-toggle-${p.id}`} onClick={() => onToggle(p)} className="p-1.5 rounded-lg hover:bg-[#1C2438] text-slate-400"><Power size={14} /></button>
          <button data-testid={`partner-delete-${p.id}`} onClick={() => onDelete(p)} className="p-1.5 rounded-lg hover:bg-[#1C2438] text-slate-400 hover:text-[#FF4D4D]"><Trash2 size={14} /></button>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-[#0B0F18] border border-[#232D42] rounded-lg px-3 py-2 mb-3">
        <KeyRound size={13} className="text-slate-500 shrink-0" />
        <code className="text-[11px] font-mono text-slate-300 truncate flex-1">{p.api_key}</code>
        <button data-testid={`partner-copy-key-${p.id}`} onClick={() => copy(p.api_key, "API key")} className="text-slate-500 hover:text-[#00E5A3] shrink-0"><Copy size={13} /></button>
      </div>

      <button data-testid={`partner-snippet-${p.id}`} onClick={() => setShowSnippet(!showSnippet)}
        className="text-xs font-mono text-[#38BDF8] hover:underline flex items-center gap-1">
        <Code2 size={13} /> {showSnippet ? "Hide" : "Embed & API"} snippet
      </button>

      {showSnippet && (
        <div className="mt-3 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1"><span className="text-[10px] font-mono uppercase text-slate-500">Embed (iframe)</span>
              <button onClick={() => copy(iframe, "Embed code")} className="text-slate-500 hover:text-[#00E5A3]"><Copy size={12} /></button></div>
            <pre className="text-[10px] font-mono text-slate-400 bg-[#0B0F18] border border-[#232D42] rounded-lg p-2.5 overflow-x-auto whitespace-pre-wrap break-all">{iframe}</pre>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><span className="text-[10px] font-mono uppercase text-slate-500">REST API</span>
              <button onClick={() => copy(apiExample, "API example")} className="text-slate-500 hover:text-[#00E5A3]"><Copy size={12} /></button></div>
            <pre className="text-[10px] font-mono text-slate-400 bg-[#0B0F18] border border-[#232D42] rounded-lg p-2.5 overflow-x-auto whitespace-pre-wrap break-all">{apiExample}</pre>
          </div>
          <a href={`/embed?key=${p.api_key}&league=NFL`} target="_blank" rel="noopener noreferrer"
            className="text-[11px] font-mono text-[#00E5A3] hover:underline inline-block">Open live preview ↗</a>
        </div>
      )}
    </div>
  );
}

export default function PartnersAdmin() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", brand_name: "", brand_color: "#00E5A3", brand_logo: "", leagues: [] });
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/partners").then((r) => setPartners(r.data.partners || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { if (user?.role === "admin") load(); else setLoading(false); }, [user]);

  const create = async () => {
    if (!form.name.trim() || !form.brand_name.trim()) { toast.error("Partner name and brand name are required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/partners", { ...form, leagues: form.leagues.length ? form.leagues : null });
      toast.success("Partner created");
      setForm({ name: "", brand_name: "", brand_color: "#00E5A3", brand_logo: "", leagues: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
    finally { setCreating(false); }
  };
  const onToggle = async (p) => { await api.put(`/admin/partners/${p.id}`, { active: !p.active }); load(); };
  const onDelete = async (p) => { if (window.confirm(`Delete ${p.brand_name}? Their embed will stop working.`)) { await api.delete(`/admin/partners/${p.id}`); load(); } };
  const toggleLeague = (lg) => setForm((f) => ({ ...f, leagues: f.leagues.includes(lg) ? f.leagues.filter((x) => x !== lg) : [...f.leagues, lg] }));

  if (user && user.role !== "admin") {
    return <div className="max-w-xl mx-auto px-4 py-20 text-center text-slate-400">This area is for administrators only.</div>;
  }

  return (
    <div data-testid="partners-admin-page" className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-100 flex items-center gap-2 mb-1">
        <Building2 size={22} className="text-[#00E5A3]" /> White-Label Partners
      </h1>
      <p className="text-sm text-slate-400 mb-6 max-w-2xl">
        Issue an API key so a media site, handicapper or affiliate can embed your cited injury board under their own brand — or pull it via REST. Every call is metered per key.
      </p>

      <div className="bg-[#0F1420] border border-[#232D42] rounded-xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-4"><Plus size={16} className="text-[#00E5A3]" /><h2 className="font-heading font-semibold text-slate-100">New partner</h2></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <input data-testid="partner-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Internal partner name (e.g. Acme Sports)" className="bg-[#151B2B] border border-[#232D42] rounded-lg px-3 py-2 text-sm text-slate-200" />
          <input data-testid="partner-brand-input" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} placeholder="Brand shown in the widget (e.g. Acme Injury Desk)" className="bg-[#151B2B] border border-[#232D42] rounded-lg px-3 py-2 text-sm text-slate-200" />
          <input data-testid="partner-logo-input" value={form.brand_logo} onChange={(e) => setForm({ ...form, brand_logo: e.target.value })} placeholder="Logo URL (optional)" className="bg-[#151B2B] border border-[#232D42] rounded-lg px-3 py-2 text-sm text-slate-200" />
          <div className="flex items-center gap-2">
            <input data-testid="partner-color-input" type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-9 w-12 bg-transparent border border-[#232D42] rounded-lg cursor-pointer" />
            <span className="text-xs font-mono text-slate-400">Accent color</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-[11px] font-mono text-slate-500">Leagues (none = all):</span>
          {ALL_LEAGUES.map((lg) => (
            <button key={lg} data-testid={`partner-league-${lg}`} onClick={() => toggleLeague(lg)}
              className={`text-xs font-mono px-2.5 py-1 rounded-lg border ${form.leagues.includes(lg) ? "text-[#00E5A3] border-[#00E5A3]/50 bg-[#00E5A3]/10" : "text-slate-400 border-[#232D42]"}`}>{lg}</button>
          ))}
        </div>
        <Button data-testid="partner-create-btn" onClick={create} disabled={creating} className="bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold">
          {creating ? "Creating…" : "Create partner key"}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500 text-sm">Loading…</div>
      ) : partners.length === 0 ? (
        <div data-testid="partners-empty" className="text-center py-12 text-slate-500 text-sm">No partners yet. Create your first key above.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {partners.map((p) => <PartnerCard key={p.id} p={p} onToggle={onToggle} onDelete={onDelete} />)}
        </div>
      )}
    </div>
  );
}
